import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Callout,
  Flex,
  LoadingState,
  MenuLinkButton,
  ServerErrorCallout,
  Text,
} from '@/components/common';
import { AccountSubpageShell } from '@/components/layout';
import { useSubscription } from '@/hooks';
import { subscriptionService } from '@/services/subscriptionService';

export function SubscriptionPage() {
  const { t } = useTranslation('subscription');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    hasSubscription,
    isCancelled,
    isPending,
    isOutOfSync,
    activeUntilDate,
    isLoading: subscriptionLoading,
    presentPaywall,
    sync,
    restore,
    manage,
  } = useSubscription();

  const [isPurchasing, setIsPurchasing] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);

  const statusParam = searchParams.get('status');
  const hasProcessed = useRef(false);
  const syncAttempted = useRef(false);

  useEffect(() => {
    if (!statusParam || hasProcessed.current) {
      return;
    }

    if (statusParam === 'success') {
      if (hasSubscription) {
        hasProcessed.current = true;
        navigate('/discover');
        return;
      }
      if (!syncAttempted.current) {
        syncAttempted.current = true;
        void sync().catch(() => {});
      }
      return;
    }

    hasProcessed.current = true;

    void (async () => {
      if (statusParam === 'cancelled') {
        try {
          await subscriptionService.cancelCheckout();
        } catch (err) {
          if ((err as { code?: string }).code !== 'functions/failed-precondition') {
            const message = err instanceof Error ? err.message : t('purchase.error');
            toast.error(message);
          }
        }
      }

      navigate('/account/subscription', { replace: true });
    })();
  }, [statusParam, hasSubscription, sync, t, navigate]);

  const handlePresentPaywall = async () => {
    setPurchaseError(null);
    setIsPurchasing(true);

    try {
      const { active } = await presentPaywall();
      if (active) {
        toast.success(t('purchase.success'));
        navigate('/discover');
      }
    } catch (err) {
      const isUserCancelled = err instanceof Error && err.message.toLowerCase().includes('cancel');

      if (!isUserCancelled) {
        const message = err instanceof Error ? err.message : t('purchase.error');
        setPurchaseError(message);
      }
    } finally {
      setIsPurchasing(false);
    }
  };

  const handleRestore = async () => {
    setPurchaseError(null);
    setIsRestoring(true);

    try {
      const { active } = await restore();
      if (active) {
        toast.success(t('purchase.success'));
      } else {
        toast.error(t('restore.noSubscription'));
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : t('purchase.error');
      setPurchaseError(message);
    } finally {
      setIsRestoring(false);
    }
  };

  const handleSync = async () => {
    setPurchaseError(null);
    setIsSyncing(true);

    try {
      const { active } = await sync();
      if (active) {
        toast.success(t('sync.success'));
      } else {
        toast.info(t('sync.noSubscription'));
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : t('purchase.error');
      setPurchaseError(message);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleManage = async () => {
    try {
      await manage();
    } catch (err) {
      const message = err instanceof Error ? err.message : t('purchase.error');
      toast.error(message);
    }
  };

  if (subscriptionLoading) {
    return (
      <AccountSubpageShell onBack={() => navigate('/account')} title={t('title')}>
        <Flex align="center" direction="column" gap="3" py="9">
          <LoadingState />
        </Flex>
      </AccountSubpageShell>
    );
  }

  return (
    <AccountSubpageShell onBack={() => navigate('/account')} title={t('title')}>
      <Flex direction="column" gap="4" pb="5">
        {purchaseError && <ServerErrorCallout message={purchaseError} />}

        <Flex direction="column" gap="1">
          <Text color="gray" size="2">
            {t('status.label')}
          </Text>
          <Text size="3" weight="bold">
            {hasSubscription ? t('status.active') : t('status.inactive')}
          </Text>
        </Flex>

        {isCancelled && activeUntilDate ? (
          <Callout>
            {t('cancellation.callout', {
              date: activeUntilDate.toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              }),
            })}
          </Callout>
        ) : null}

        {isPending && <Callout>{t('pending.message')}</Callout>}

        <Flex direction="column" gap="2">
          {!hasSubscription && !isPending && (
            <Button fullWidth isLoading={isPurchasing} onClick={() => void handlePresentPaywall()}>
              {t('paywall.cta')}
            </Button>
          )}

          {!hasSubscription && isOutOfSync && (
            <Button
              fullWidth
              isLoading={isRestoring}
              onClick={() => void handleRestore()}
              variant="soft"
            >
              {t('restore.cta')}
            </Button>
          )}

          {hasSubscription && (
            <MenuLinkButton
              subtitle={t('manage.subtitle')}
              title={t('manage.title')}
              onClick={() => void handleManage()}
            />
          )}

          {(isPending || isOutOfSync) && (
            <Button
              fullWidth
              isLoading={isSyncing}
              onClick={() => void handleSync()}
              variant="soft"
            >
              {t('sync.cta')}
            </Button>
          )}
        </Flex>
      </Flex>
    </AccountSubpageShell>
  );
}
